import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Dialog, Snackbar } from "@core/material";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import * as moment from "moment";
import { AdvancedReportCompilerService } from "@core/data/api/reports/advanced-report-compiler.service";
import { BirthDateSort, CultivarGroup, DeleteAsset, EvalGroup, ImagePickerTab, ImageTabSlice, ImageTile, InitReportImageForm, LoadImageFormOptions, LoadMoreImageOptions, NamedUpload, RenameAsset, ReportImageFormState, SampleGroup, SiteGroup, SetActiveTab, SetBirthDateSort, SetImageFilter, SetSortAxis, SortAxis, toFilterPatch, UploadAssets } from "./report-image-edit-form.state";
import { ReportService, Status, isErrorStatus } from "@core/data";
import { ReportDataContext, ReportImageOption } from "@core/data/types/advanced-report";
import { AuthState } from "@app/auth";
import { fileExtension } from "@core/utils";
import { previewDataFor, previewDataForAsset, ReportImagePreviewDialog, ReportImagePreviewResult } from "./report-image-preview.dialog";
import { debounceTime, distinctUntilChanged, map, takeUntil } from "rxjs/operators";

// AC 2 — must match the server's validator exactly (jpg/jpeg/png/gif/webp, max:40000 KB)
const REPORT_UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const REPORT_UPLOAD_MAX_BYTES = 40000 * 1024;

// Name is no longer typed by the user — derived from the filename and lightly cleaned up.
// Renaming afterward (once the asset exists) is handled by the tile's inline rename affordance.
function nameFromFilename(filename: string): string {
    let base = filename.replace(/\.[^./\\]+$/, '');
    base = base.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    return (base || 'Untitled image').slice(0, 255);
}

// Shared by the Upload and Logos & Graphics tabs — both post through the same server-side validator (AC 2).
function fileUploadError(file: File): string | null {
    let ext = fileExtension(file.name).toLowerCase();
    if (REPORT_UPLOAD_EXTENSIONS.indexOf(ext) === -1) return `${file.name} is not a valid image type.`;
    if (file.size > REPORT_UPLOAD_MAX_BYTES) return `${file.name} exceeds the maximum file size.`;
    return null;
}

export interface ReportImageEditFormDialogData {
    imageKey: string;
    reportKey: string;
    ownerOrgKey: string;
    /** The component's currently-stored context, so the dialog can open on the matching tab (AD-7). */
    context?: ReportDataContext;
}

@Component({
    selector: 'pv-report-image-edit-form-dialog',
    templateUrl: 'report-image-edit-form.dialog.html',
    styleUrls:   ['report-image-edit-form.dialog.scss'],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'pv-report-image-edit-form-dialog',
    }
})
export class ReportImageEditFormDialog implements OnInit, OnDestroy {

    @Select(ReportImageFormState.activeSlice)
    activeSlice$: Observable<ImageTabSlice>;

    @Select(ReportImageFormState.activeTab)
    activeTab$: Observable<ImagePickerTab>;

    @Select(ReportImageFormState.sampleTree)
    sampleTree$: Observable<CultivarGroup[]>;

    @Select(ReportImageFormState.birthDateSort)
    birthDateSort$: Observable<BirthDateSort>;

    @Select(ReportImageFormState.sortAxis)
    sortAxis$: Observable<SortAxis>;

    @Select(ReportImageFormState.birthYearOptions)
    birthYearOptions$: Observable<number[]>;

    filterGroup: FormGroup = new FormGroup({
        cultivars: new FormControl([]),
        sites: new FormControl([]),
        labelSearch: new FormControl(null),
        birthYear: new FormControl(null),
        evalFrom: new FormControl(null),
        evalTo: new FormControl(null),
    });

    get orgKey(): string {
        return this._data.ownerOrgKey;
    }

    readonly Status = Status;
    readonly ImagePickerTab = ImagePickerTab;

    readonly tabs: ImagePickerTab[] = [ImagePickerTab.SAMPLE, ImagePickerTab.ORG, ImagePickerTab.REPORT];

    // mirrors the server's ASSET_WRITE_ROLES exactly — client and server must never drift on who counts as read-only.
    // Looks up the REPORT's own owning org (this._data.ownerOrgKey), not AuthState.selectedOrg (the session's
    // globally-active org) — those two differ for a user viewing a report that belongs to a different org than
    // whichever one is currently "selected" elsewhere in the app, which previously gated buttons on the wrong
    // org's role (code review finding).
    get canWriteAssets(): boolean {
        let orgs = this._store.selectSnapshot(AuthState.availableOrgs) || [];
        let role = orgs.find(org => org.key === this._data.ownerOrgKey)?.access?.role;
        return ['rw', 'admin', 'owner'].indexOf(role) !== -1;
    }

    // placeholder squares that hold the grid's shape while the request is in flight
    // one full grid row at the spec'd 124px column width
    readonly skeletonTiles: null[] = new Array(5).fill(null);
    readonly skeletonGroups: null[] = new Array(2).fill(null);

    private _activeTab: ImagePickerTab = ImagePickerTab.SAMPLE;
    private _sort: BirthDateSort = 'asc';

    imageControl: FormControl = new FormControl(null, [Validators.required]);
    imageUrl: FormControl = new FormControl(null, [Validators.required]);

    formGroup: FormGroup = new FormGroup({
        data: this.imageControl,
    });

    // Logos & Graphics tab — upload is immediate (POST per drop/browse); multiple files may be dropped/browsed
    // at once.
    isDraggingOrgFile = false;
    orgUploadPending = false;
    orgUploadErrors: string[] = [];
    // count of files in the in-flight batch — drives how many skeleton tiles render at the end of the grid
    orgUploadingCount = 0;

    // Inline rename affordance shared by the ORG and REPORT tabs' existing-asset tiles.
    renamingKey: string = null;
    renameControl: FormControl = new FormControl(null, [Validators.required, Validators.maxLength(255), Validators.pattern(/\S/)]);
    renamePending = false;

    // Shared by the ORG and REPORT tabs — keys currently mid-delete, so their tile can show a skeleton/dim
    // state instead of just sitting there unchanged until the response lands.
    deletingKeys = new Set<string>();

    /** True once we've confirmed the org has legacy report_letterhead rows — drives the empty state's addendum (F-4.9). */
    hasLegacyLetterheads = false;

    // Upload tab — upload is immediate (POST per drop/browse), same as Logos & Graphics: a report-scoped
    // asset is committed the moment it's uploaded, not staged behind Apply.
    isDraggingReportFile = false;
    reportUploadPending = false;
    reportUploadErrors: string[] = [];
    reportUploadingCount = 0;

    private _destroy$ = new Subject();

    constructor(
        private _dialogRef: MatDialogRef<ReportImageEditFormDialogData>,
        @Inject(MAT_DIALOG_DATA) private _data: ReportImageEditFormDialogData,
        private _snackbar: Snackbar,
        private _store: Store,
        private _dialog: Dialog,
        private _reportService: ReportService,
        private _compilerService: AdvancedReportCompilerService,
        private _changeRef: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this._store.dispatch(new InitReportImageForm(this._data.reportKey, this._data.ownerOrgKey, this._data.imageKey));

        // reopen on the tab matching the stored context (AD-7) — Init always defaults to SAMPLE first
        if (this._data.context === ReportDataContext.ORG_ASSET) {
            this._store.dispatch(new SetActiveTab(ImagePickerTab.ORG));
        } else if (this._data.context === ReportDataContext.REPORT_ASSET) {
            this._store.dispatch(new SetActiveTab(ImagePickerTab.REPORT));
        }

        this._reportService.getOrgReportLetterheads(this._data.ownerOrgKey)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: letterheads => {
                    this.hasLegacyLetterheads = (letterheads || []).length > 0;
                    this._changeRef.markForCheck();
                },
                // best-effort addendum copy — a failed probe just means the plain empty-state text shows instead
                error: () => {},
            });

        this.activeSlice$.pipe(takeUntil(this._destroy$))
            .subscribe(slice => {
                if (this.imageControl.value == null && slice.status === Status.OK && slice.options.length > 0) this.reset(slice.options);
            });

        this.activeTab$.pipe(takeUntil(this._destroy$))
            .subscribe(tab => this._activeTab = tab);

        this.birthDateSort$.pipe(takeUntil(this._destroy$))
            .subscribe(sort => this._sort = sort);

        // debounced + de-duped: each dispatch mints a new filter object, which re-filters and regroups the whole payload
        this.filterGroup.valueChanges.pipe(
            debounceTime(200),
            map(value => toFilterPatch(value)),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            takeUntil(this._destroy$),
        ).subscribe(patch => this._store.dispatch(new SetImageFilter(patch)));

        // a selection filtered out of view must not stay silently armed behind the Apply button
        this.sampleTree$.pipe(takeUntil(this._destroy$))
            .subscribe(tree => {
                let selected = this.imageControl.value;
                if (selected && !this._treeContains(tree, selected.key)) {
                    this.imageControl.setValue(null);
                    this._changeRef.markForCheck();
                }
            });
    }

    private _treeContains(tree: CultivarGroup[], key: string): boolean {
        return (tree || []).some(cultivar =>
            cultivar.sites.some(site =>
                site.samples.some(sample =>
                    sample.evals.some(evaluation =>
                        evaluation.images.some(tile => tile.option.key === key)))));
    }

    clearFilters() {
        this.filterGroup.reset({ cultivars: [], sites: [], labelSearch: null, birthYear: null, evalFrom: null, evalTo: null });
    }

    isLoading(status: Status): boolean {
        return status === Status.LOADING || status === Status.UNINITIALIZED;
    }

    isReady(status: Status): boolean {
        return status === Status.OK || status === Status.EMPTY;
    }

    // benign empty is not an error state — everything else isErrorStatus flags is
    isError(status: Status): boolean {
        return isErrorStatus(status) && status !== Status.EMPTY;
    }

    selectTab(tab: ImagePickerTab) {
        // an in-progress rename must not carry over — asset keys are globally unique so it's not
        // exploitable, but leaving it armed means starting a rename on ORG, switching to REPORT, then
        // back to ORG reopens the same edit box as if nothing happened
        if (this.renamingKey) this.cancelRename();

        this._store.dispatch(new SetActiveTab(tab));
    }

    // F-11.2's chip vocabulary — icon + label per tab; REPORT is unused until story 2.4 adds it to `tabs`
    tabIcon(tab: ImagePickerTab): string {
        switch (tab) {
            case ImagePickerTab.ORG: return 'imagesmode';
            case ImagePickerTab.REPORT: return 'upload';
            default: return 'photo_library';
        }
    }

    tabLabel(tab: ImagePickerTab): string {
        switch (tab) {
            case ImagePickerTab.ORG: return 'Logos & Graphics';
            case ImagePickerTab.REPORT: return 'Upload';
            default: return 'Sample Photos';
        }
    }

    retry() {
        this._store.dispatch(new LoadImageFormOptions(this._activeTab));
    }

    // Infinite scroll — all three tabs are server-paginated now (LoadMoreImageOptions no-ops harmlessly
    // if a tab's slice has hasMore false, e.g. once a small library's single page has fully loaded).
    // 400px from the bottom gives the next page's request time to land before the user actually hits the
    // end of the currently-rendered tiles.
    private static readonly LOAD_MORE_THRESHOLD_PX = 400;

    onBodyScroll(event: Event) {
        let el = event.target as HTMLElement;
        let remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining > ReportImageEditFormDialog.LOAD_MORE_THRESHOLD_PX) return;

        this._store.dispatch(new LoadMoreImageOptions(this._activeTab));
    }

    setSort(sort: BirthDateSort) {
        this._store.dispatch(new SetBirthDateSort(sort));
    }

    setAxis(axis: SortAxis) {
        this._store.dispatch(new SetSortAxis(axis));
    }

    toggleSort() {
        this._store.dispatch(new SetBirthDateSort(this._sort === 'asc' ? 'desc' : 'asc'));
    }

    // names the direction currently applied, on whichever axis is active
    sortToggleLabel(axis: SortAxis, sort: BirthDateSort): string {
        if (axis === 'label') return sort === 'asc' ? 'A → Z' : 'Z → A';
        return sort === 'asc' ? 'Oldest first' : 'Newest first';
    }

    trackSite(index: number, group: SiteGroup) { return group.siteKey || 'no-site'; }

    // reuses the preview's own caption so the button's announced date can never disagree with the dialog it opens
    previewLabel(tile: ImageTile): string {
        return `Preview image — ${previewDataFor(tile).caption}`;
    }

    selectLabel(tile: ImageTile): string {
        return `Select image — ${previewDataFor(tile).caption}`;
    }

    // Enter/Space select the tile. Events bubbling up from the nested magnifier are ignored,
    // otherwise activating it by keyboard would both preview and select.
    onTileKeydown(tile: ImageTile, event: KeyboardEvent) {
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

        event.preventDefault();
        this.selectImage(tile.option);
    }

    // the magnifier sits on top of the tile's select surface — swallow the click so previewing never selects (F-8.2)
    openPreview(tile: ImageTile, event: Event) {
        event.stopPropagation();

        this._dialog.open(ReportImagePreviewDialog, {
            data: previewDataFor(tile),
            panelClass: 'reporting-theme',
            width: '900px',
            maxWidth: '95vw',
            autoFocus: false,
        })
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((result: ReportImagePreviewResult) => {
            if (result && result.select) this.selectImage(tile.option);
        });
    }

    // identity tracking: without it a re-sort recreates every tile and re-fetches its thumbnail
    trackCultivar(index: number, group: CultivarGroup) { return group.cultivarKey || 'unknown'; }
    trackSample(index: number, group: SampleGroup) { return group.sampleKey; }
    trackEval(index: number, group: EvalGroup) { return group.evalKey; }
    trackTile(index: number, tile: ImageTile) { return tile.option.key; }
    trackAssetOption(index: number, option: ReportImageOption) { return option.key; }

    // Logos & Graphics tab — flat grid, no grouping (AD-8/F-4.1); assets aren't wrapped as ImageTile
    assetPreviewLabel(option: ReportImageOption): string {
        return `Preview image — ${previewDataForAsset(option).caption}`;
    }

    assetSelectLabel(option: ReportImageOption): string {
        return `Select image — ${previewDataForAsset(option).caption}`;
    }

    onAssetKeydown(option: ReportImageOption, event: KeyboardEvent) {
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

        event.preventDefault();
        this.selectImage(option);
    }

    openAssetPreview(option: ReportImageOption, event: Event) {
        event.stopPropagation();

        this._dialog.open(ReportImagePreviewDialog, {
            data: previewDataForAsset(option),
            panelClass: 'reporting-theme',
            width: '900px',
            maxWidth: '95vw',
            autoFocus: false,
        })
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((result: ReportImagePreviewResult) => {
            if (result && result.select) this.selectImage(option);
        });
    }

    // Logos & Graphics tab — drag/drop plumbing mirrors the Upload tab's, but nothing is staged: each valid
    // file uploads immediately as a batch, since an org asset is a library item, not the current selection.

    onOrgDragOver(event: DragEvent) {
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        if (!this.isDraggingOrgFile) this.isDraggingOrgFile = true;
        event.preventDefault();
        event.stopPropagation();
    }

    onOrgDragLeave(event: DragEvent) {
        if (this.isDraggingOrgFile) this.isDraggingOrgFile = false;
        event.preventDefault();
    }

    onOrgDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDraggingOrgFile = false;

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
            this.uploadFiles(ImagePickerTab.ORG, event.dataTransfer.files);
        }
    }

    onOrgFileSelected(event: Event) {
        let input = event.target as HTMLInputElement;
        this.uploadFiles(ImagePickerTab.ORG, input.files);
        input.value = null; // allows re-selecting the same file(s) after an error
    }

    // Shared by Logos & Graphics and the Upload tab — both upload immediately, as a batch, straight into
    // their own tab's slice. The two tabs keep separate pending/error fields (template clarity, and so an
    // in-flight upload on one tab never disables the other's dropzone) but the upload logic itself is one path.
    private uploadFiles(tab: ImagePickerTab, files: FileList) {
        let pending = tab === ImagePickerTab.REPORT ? this.reportUploadPending : this.orgUploadPending;
        if (pending) return;
        if (!files || !files.length) return;

        let errors: string[] = [];
        let uploads: NamedUpload[] = [];

        Array.from(files).forEach(file => {
            let error = fileUploadError(file);
            if (error) {
                errors.push(error);
                return;
            }
            uploads.push({ name: nameFromFilename(file.name), file });
        });

        if (tab === ImagePickerTab.REPORT) this.reportUploadErrors = errors;
        else this.orgUploadErrors = errors;
        this._changeRef.markForCheck();
        if (!uploads.length) return;

        if (tab === ImagePickerTab.REPORT) {
            this.reportUploadPending = true;
            this.reportUploadingCount = uploads.length;
        } else {
            this.orgUploadPending = true;
            this.orgUploadingCount = uploads.length;
        }
        this._changeRef.markForCheck();

        this._store.dispatch(new UploadAssets(tab, uploads))
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => {
                    if (tab === ImagePickerTab.REPORT) {
                        this.reportUploadPending = false;
                        this.reportUploadingCount = 0;
                    } else {
                        this.orgUploadPending = false;
                        this.orgUploadingCount = 0;
                    }
                    this._changeRef.markForCheck();
                },
                error: () => {
                    if (tab === ImagePickerTab.REPORT) {
                        this.reportUploadPending = false;
                        this.reportUploadingCount = 0;
                    } else {
                        this.orgUploadPending = false;
                        this.orgUploadingCount = 0;
                    }
                    this._changeRef.markForCheck();
                },
            });
    }

    // fresh array each call, sized to N — used to *ngFor a fixed count of skeleton tiles
    uploadingPlaceholders(count: number): null[] {
        return new Array(count).fill(null);
    }

    // Inline rename — shared by the ORG and REPORT tabs' existing-asset tiles. Staged (not-yet-uploaded)
    // items have no server key yet, so they're never renamable; only committed assets can be.

    isRenaming(option: ReportImageOption): boolean {
        return this.renamingKey === option.key;
    }

    startRename(option: ReportImageOption, event: Event) {
        event.stopPropagation();
        if (this.renamePending) return;

        this.renamingKey = option.key;
        this.renameControl.setValue(option.name);
        this._changeRef.markForCheck();
    }

    cancelRename(event?: Event) {
        if (event) event.stopPropagation();
        this.renamingKey = null;
        this._changeRef.markForCheck();
    }

    onRenameKeydown(option: ReportImageOption, event: KeyboardEvent) {
        event.stopPropagation();
        if (event.key === 'Escape') return this.cancelRename();
        if (event.key === 'Enter') this.commitRename(option, event);
    }

    commitRename(option: ReportImageOption, event: Event) {
        event.stopPropagation();
        if (this.renamePending) return;

        this.renameControl.updateValueAndValidity();
        if (!this.renameControl.valid) return this._snackbar.error('A name is required');

        let name = (this.renameControl.value || '').trim();
        if (name === (option.name || '')) return this.cancelRename();

        this.renamePending = true;
        this._changeRef.markForCheck();

        this._store.dispatch(new RenameAsset(this._activeTab, option.key, name))
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => {
                    this.renamePending = false;
                    this.renamingKey = null;
                    // the selection carries its own copy of `name` (F-8.6's caption) — keep it in sync post-rename
                    if (this.imageControl.value?.key === option.key) {
                        this.imageControl.setValue({ ...this.imageControl.value, name });
                    }
                    this._changeRef.markForCheck();
                },
                error: () => {
                    this.renamePending = false;
                    this._changeRef.markForCheck();
                },
            });
    }

    // Shared by the ORG and REPORT tabs' existing-asset tiles — the server's delete endpoint is identical
    // for both scopes, so the only per-tab difference is which slice to patch (this._activeTab).
    deleteAsset(option: ReportImageOption, event: Event) {
        event.stopPropagation();

        this._dialog.confirm(
            'Delete Image',
            "Reports already using this image will keep it. Deleting only removes it from the library — it won't be available to select anymore.",
            'Delete',
            'Cancel',
            true,
        ).afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe(result => {
            if (!result) return;

            this.deletingKeys.add(option.key);
            this._changeRef.markForCheck();

            this._store.dispatch(new DeleteAsset(this._activeTab, option.key))
                .pipe(takeUntil(this._destroy$))
                .subscribe({
                    // a deleted tile must not stay armed behind Apply — same principle as the sample
                    // tab's filtered-out-of-view guard, just triggered by a delete instead of a filter
                    next: () => {
                        this.deletingKeys.delete(option.key);
                        if (this.imageControl.value?.key === option.key) {
                            this.imageControl.setValue(null);
                        }
                        this._changeRef.markForCheck();
                    },
                    error: () => {
                        this.deletingKeys.delete(option.key);
                        this._changeRef.markForCheck();
                    },
                });
        });
    }

    isDeleting(option: ReportImageOption): boolean {
        return this.deletingKeys.has(option.key);
    }

    // Upload tab — drag/drop plumbing mirrors the Logos & Graphics tab's: each valid file uploads
    // immediately as a batch, straight into the report's own asset slice.

    onReportDragOver(event: DragEvent) {
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        if (!this.isDraggingReportFile) this.isDraggingReportFile = true;
        event.preventDefault();
        event.stopPropagation();
    }

    onReportDragLeave(event: DragEvent) {
        if (this.isDraggingReportFile) this.isDraggingReportFile = false;
        event.preventDefault();
    }

    onReportDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDraggingReportFile = false;

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
            this.uploadFiles(ImagePickerTab.REPORT, event.dataTransfer.files);
        }
    }

    onReportFileSelected(event: Event) {
        let input = event.target as HTMLInputElement;
        this.uploadFiles(ImagePickerTab.REPORT, input.files);
        input.value = null; // allows re-selecting the same file(s) after an error
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    selectImage(option: ReportImageOption) {
        this.imageControl.setValue(option);
        // OnPush: a selection made from an async callback (the preview) repaints nothing without this
        this._changeRef.markForCheck();
    }

    reset(options: ReportImageOption[]) {
        let currentImage = options.find(option => option.key === this._data.imageKey);

        this.formGroup.reset({
            data: currentImage,
        })

        this.formGroup.enable()

        // called from activeSlice$'s manual subscribe (ngOnInit), not a template `| async` — under OnPush,
        // setting imageControl's value here doesn't repaint the tiles' [class.active] binding until some
        // unrelated async pipe fires later (e.g. switching tabs), which is exactly the bug this fixes
        this._changeRef.markForCheck();
    }

    attempt() {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAsDirty();

        if (!this.formGroup.valid) return this._snackbar.error("Form Invalid, an image is required");

        this._dialogRef.close({
            status: true,
            data: this.formGroup.value.data
        });
    }

    cancel() {
        this._dialogRef.close({status: false});
    }
}